// src/theme.js
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

export const OASYS_THEME_STORAGE_KEY = "theme";
export const OASYS_LEGACY_THEME_STORAGE_KEY = "themeMode";
export const OASYS_DEFAULT_THEME_PREFERENCE = "dark";
export const OASYS_THEME_PREFERENCES = ["dark", "light", "system"];

function normalizeThemePreference(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return OASYS_THEME_PREFERENCES.includes(normalized)
    ? normalized
    : OASYS_DEFAULT_THEME_PREFERENCE;
}

export function getOasysThemePreference() {
  if (typeof window === "undefined") {
    return OASYS_DEFAULT_THEME_PREFERENCE;
  }

  try {
    const stored =
      window.localStorage.getItem(OASYS_THEME_STORAGE_KEY) ||
      window.localStorage.getItem(OASYS_LEGACY_THEME_STORAGE_KEY);

    if (!stored) return OASYS_DEFAULT_THEME_PREFERENCE;
    return normalizeThemePreference(stored);
  } catch {
    return OASYS_DEFAULT_THEME_PREFERENCE;
  }
}

export function resolveOasysThemePreference(preference) {
  const normalized = normalizeThemePreference(preference);

  if (normalized === "light" || normalized === "dark") {
    return normalized;
  }

  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }

  return "light";
}

export function getOasysEffectiveTheme() {
  if (typeof document !== "undefined") {
    const rootTheme = document.documentElement?.dataset?.theme;
    if (rootTheme === "light" || rootTheme === "dark") {
      return rootTheme;
    }
  }

  return resolveOasysThemePreference(getOasysThemePreference());
}

export function applyOasysThemePreference(
  preference,
  { persist = true, emit = persist } = {}
) {
  const normalized = normalizeThemePreference(preference);
  const effectiveTheme = resolveOasysThemePreference(normalized);

  if (typeof document !== "undefined") {
    const root = document.documentElement;
    root.dataset.theme = effectiveTheme;
    root.dataset.themePreference = normalized;
  }

  if (persist && typeof window !== "undefined") {
    try {
      window.localStorage.setItem(OASYS_THEME_STORAGE_KEY, normalized);
      window.localStorage.removeItem(OASYS_LEGACY_THEME_STORAGE_KEY);
    } catch {
      // Theme remains active for the current session if storage is unavailable.
    }
  }

  if (emit && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("oasys:theme-change", {
        detail: {
          preference: normalized,
          effectiveTheme,
        },
      })
    );
  }

  return {
    preference: normalized,
    effectiveTheme,
  };
}

export function initializeOasysTheme() {
  return applyOasysThemePreference(getOasysThemePreference(), {
    persist: false,
    emit: false,
  });
}
